/**
 * 🛠️ AdminHeroSwitcher — Owner-only "hop between hero classes" widget.
 *
 * Renders inline in the Arena landing header (or wherever slotted). Fetches
 * the full class list from `/api/hero-class/state` (which returns the full
 * catalog under `.classes`) and lets the owner swap their class instantly
 * via `POST /api/hero-class/admin/switch-class`. Bypasses V1 no-swap and
 * gated-class locks — used only for testing SkillPicker + kit rendering
 * across every hero.
 *
 * Visibility: renders `null` for anyone whose /api/auth/me role !== 'owner'.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { fireAwakeningReveal } from "@/components/AwakeningRevealCinematic";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminHeroSwitcher({ onSwitched, compact = false }) {
  const [isOwner, setIsOwner] = useState(false);
  const [classes, setClasses] = useState([]);
  const [currentClass, setCurrentClass] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await axios.get(`${API}/auth/me`);
        if (cancelled) return;
        if ((me.data?.role || "").toLowerCase() !== "owner") {
          setIsOwner(false);
          return;
        }
        setIsOwner(true);
        // /hero-class/state returns { class_id, level, classes_catalog: [{id,name,icon,gated,...}, ...] }
        const state = await axios.get(`${API}/hero-class/state`);
        if (cancelled) return;
        setCurrentClass(state.data?.class_id || null);
        const catalog = state.data?.classes_catalog || [];
        const items = catalog.map((meta) => ({
          id:    meta?.id,
          name:  meta?.name || meta?.id,
          icon:  meta?.icon || "✨",
          gated: !!meta?.gated,
        })).filter((c) => c.id);
        // Alphabetize by display name for predictable navigation
        items.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setClasses(items);
      } catch {
        setIsOwner(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const doSwitch = async (classId) => {
    if (!classId || classId === currentClass || busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/hero-class/admin/switch-class`, {
        class_id: classId,
      });
      if (r.data?.ok) {
        setCurrentClass(r.data.class_id);
        const meta = classes.find((c) => c.id === classId);
        toast.success(`🦸 Switched to ${meta?.name || classId}`);
        onSwitched?.(r.data.class_id, r.data);
      } else {
        toast.error(r.data?.detail || "Switch failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Switch failed");
    } finally {
      setBusy(false);
    }
  };

  if (!isOwner) return null;
  if (classes.length === 0) return null;

  return (
    <div
      className={
        compact
          ? "inline-flex items-center gap-1.5"
          : "bg-black/60 border border-amber-500/40 p-2 flex items-center gap-2"
      }
      data-testid="admin-hero-switcher"
    >
      <span
        className="text-[9px] uppercase tracking-widest text-amber-400 font-bold shrink-0"
        title="Owner-only class switcher"
      >
        🛠️ Admin · Class
      </span>
      <select
        value={currentClass || ""}
        onChange={(e) => doSwitch(e.target.value)}
        disabled={busy}
        data-testid="admin-hero-switcher-select"
        className="bg-black border border-amber-600/50 text-white text-xs px-1.5 py-1 outline-none focus:border-amber-400 disabled:opacity-50"
      >
        <option value="">— pick class —</option>
        {classes.map((c) => (
          <option key={c.id} value={c.id}>
            {c.icon} {c.name}{c.gated ? " 🔒" : ""}
          </option>
        ))}
      </select>
      {/* ✨ Preview the current hero's Awakening cinematic — owner-only
          dry-run so we can verify the reveal timeline + art without
          waiting for an in-game trigger. */}
      {currentClass && (
        <button
          type="button"
          onClick={() => fireAwakeningReveal({ hero_id: currentClass })}
          data-testid="admin-preview-awakening"
          title="Preview this hero's Awakening reveal cinematic"
          className="text-[9px] uppercase tracking-widest font-bold text-amber-300 hover:text-amber-200 border border-amber-600/50 hover:border-amber-400 px-1.5 py-1 transition"
        >
          ✨ Preview
        </button>
      )}
    </div>
  );
}
